import { z } from 'zod';
import { ACCU_DEFAULT_GROWTH, ACCU_GROWTH_RATES, ACCU_MAX_CASHOUT_TICKS, accuMultiplier, formatProfitPct, type AccuGrowthRate } from '../accumulator';
import type { Strategy } from './types';

/**
 * Decolagem robots. They bet in the rounds like a player (one bet per round, bought for the takeoff).
 * The user picks the growth per tick, when the robot cashes out (after N ticks inside the corridor,
 * sent to Deriv as a take profit so Deriv closes it even if we are offline) and which rounds it plays:
 *  - ALWAYS: every round;
 *  - AFTER_BREAK: after a short flight — the previous round fell before the robot's cash-out;
 *  - CALM: when the price has already stayed inside the corridor for `calmTicks` ticks in a row.
 * A gale (loss recovery) bets in the next round, whatever the mode, like the other robots.
 * Which round it plays never changes the odds of the next ticks.
 */
export type AccuEntryMode = 'ALWAYS' | 'AFTER_BREAK' | 'CALM';

export const ACCU_ENTRY_MODES: readonly { key: AccuEntryMode; label: string; hint: string }[] = [
  { key: 'ALWAYS', label: 'Toda rodada', hint: 'Entra em todas as rodadas.' },
  { key: 'AFTER_BREAK', label: 'Após rodada abaixo do saque', hint: 'Entra depois de uma rodada que caiu antes do seu saque.' },
  { key: 'CALM', label: 'Mercado calmo', hint: 'Entra quando a cotação já está há vários ticks dentro da faixa.' },
];

export interface AccuBotParams {
  growthRate: AccuGrowthRate;
  /** Cash out after this many ticks inside the corridor. */
  cashoutTicks: number;
  entry: AccuEntryMode;
  /** CALM: ticks inside the corridor in a row before entering. */
  calmTicks: number;
}

export const ACCU_BOT_DEFAULTS: AccuBotParams = { growthRate: ACCU_DEFAULT_GROWTH, cashoutTicks: 5, entry: 'ALWAYS', calmTicks: 10 };

export const ACCU_CALM_LIMITS = { min: 2, max: 60 } as const;

const paramsSchema = z
  .object({
    growthRate: z.number().refine((v): v is AccuGrowthRate => (ACCU_GROWTH_RATES as readonly number[]).includes(v), 'Crescimento deve ser de 1% a 5%'),
    cashoutTicks: z.number().int().min(1).max(ACCU_MAX_CASHOUT_TICKS),
    entry: z.enum(['ALWAYS', 'AFTER_BREAK', 'CALM']),
    calmTicks: z.number().int().min(ACCU_CALM_LIMITS.min).max(ACCU_CALM_LIMITS.max),
  })
  .strict() as unknown as z.ZodType<AccuBotParams>;

export const accumulatorBot: Strategy<AccuBotParams, Record<string, never>> = {
  key: 'invexa_accu',
  name: 'Robô Rush',
  description: 'Entra nas rodadas do Rush e saca sozinho no lucro (%) escolhido.',
  modalities: ['ACCUMULATOR'],
  paramsSchema,
  defaultParams: () => ({ ...ACCU_BOT_DEFAULTS }),
  initialState: () => ({}),
  onTick: (ctx) => {
    const p = ctx.params;
    const target = formatProfitPct(accuMultiplier(p.growthRate, p.cashoutTicks));
    const plan = `Saque em ${target}`;
    const level = ctx.level ?? 0;
    if (level > 0) return { intent: { direction: 'ACCU' }, state: ctx.state, reason: `Gale ${level}: ${plan}` };
    const market = ctx.accu;
    switch (p.entry) {
      case 'ALWAYS':
        return { intent: { direction: 'ACCU' }, state: ctx.state, reason: `Toda rodada: ${plan}` };
      case 'AFTER_BREAK': {
        const last = market?.lastFlight;
        if (last !== null && last !== undefined && last < p.cashoutTicks) return { intent: { direction: 'ACCU' }, state: ctx.state, reason: `Rodada anterior caiu em ${formatProfitPct(accuMultiplier(p.growthRate, last))} → ${plan}` };
        return { intent: null, state: ctx.state, reason: `Aguardando uma rodada cair antes de ${target}` };
      }
      case 'CALM':
        if (market && market.run >= p.calmTicks) return { intent: { direction: 'ACCU' }, state: ctx.state, reason: `${market.run} ticks dentro da faixa → ${plan}` };
        return { intent: null, state: ctx.state, reason: `Aguardando ${p.calmTicks} ticks dentro da faixa` };
      default:
        return { intent: null, state: ctx.state, reason: 'Modo de entrada desconhecido' };
    }
  },
  onSettlement: (ctx) => ctx.state,
};

/** Strategy key of the Acumulador robot. */
export const ACCU_STRATEGY_KEY = accumulatorBot.key;
